import { connect } from 'react-redux';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import BaseFeature from './BaseFeature';
import CardMasonry from './components/CardMasonry';
import FeatureCard from './components/FeatureCard';
import PropertyDropdown from './modern/PropertyDropdown';
import PropertyPicker from './modern/PropertyPicker';
import PropertySwitch from './modern/PropertySwitch';
import PropertyTimePicker from './modern/PropertyTimePicker';
import { cn } from 'lib/utils';
import { SOLAR_CHARGE_CONTROLLER as MPPT } from '../../../../../../utils/constantFeatures';
import { getErrorIssues, getWarningIssues } from '../../../../../../utils/validationSummary';

/**
 * Solar charge controller of MPPT drivers (am58962, GTIN 6942736285921).
 *
 * Built from the MPPTData SubDevice, so the feature exists only on drivers that carry
 * one, and it is independent of the operating mode — it appears in all five.
 *
 * Labels and units are NOT written here. They come from the fea file via
 * property.info.displayName / property.info.unit, which ddf2ffconverter's
 * converters/mppt.py supplies (54 of the 57 dd properties have no <Unit> element, and
 * several dd <Display><Name> strings are wrong, so the builder is the single source).
 * That keeps one name per parameter instead of one here and one in the converter.
 *
 * Resolution comes from the dd Multiplier, which t4tapi does NOT expose on valueMeta —
 * only min/max/default/off/initial, already converted to user units. So `step` has to be
 * stated per row: 0.01 for the voltages and currents (Multiplier="0.01"), 1 for the
 * temperatures, delays, percentages and milliohms.
 *
 * Visibility follows app_settings.xlsx column 14, which states each field's rule ("Shown
 * only when Battery Type is Lead-acid", "Shown only when Lighting Mode is not 0"). A
 * selector the driver does not carry falls back to showing everything rather than silently
 * hiding registers - an error on a hidden field is still reported against the feature in
 * the side panel, so visibility does not have to defend against them.
 */
class SolarChargeController extends BaseFeature {

    constructor(props) {
        super(props);
        // Which half of "Auto" the System Voltage dropdown is showing. See systemVoltage()
        // below: it is a view choice, not a register, so it is component state and resets
        // to 12V when the screen is reopened.
        this.state = { autoBand: '12V' };
        this.handleChange = this.handleChange.bind(this);
        this.handleSystemVoltage = this.handleSystemVoltage.bind(this);
    }

    handleChange(propertyName, value) {
        // Straight passthrough: this screen has no cross-field clamping of its own, and needs
        // none. Every battery voltage register carries its own band in the dd file, so
        // PropertyMinMaxRule enforces the right range without any cross-property rule.
        this.props.handleChange(propertyName, value);
    }

    // Same error/warning signal as BaseFeature.getClassName, minus the legacy
    // .card-feature-component base class — that one is `width: fit-content` and would
    // beat the modern card's `w-full`.
    cardState(properties) {
        if (getErrorIssues(this.props.issues, properties).length > 0) return 'danger';
        if (getWarningIssues(this.props.issues, properties).length > 0) return 'warning';
        return '';
    }

    get(key) {
        return this.props.properties[key];
    }

    label(p) {
        const name = p.info.displayName;
        return this.props.t(name.replaceAll(' ', '_'), { defaultValue: name });
    }

    /** The same lookup for enum option text, passed to PropertyDropdown as
     *  propertyDisplayFunc rather than its `translate` flag - translate has no fallback and
     *  would render a missing key as "PV_Priority". */
    tr(value) {
        return typeof value === 'string'
            ? this.props.t(value.replaceAll(' ', '_'), { defaultValue: value })
            : value;
    }

    /** The properties of a card that actually exist, for cardState(). */
    present(keys) {
        return keys.map((k) => this.get(k)).filter(Boolean);
    }

    /**
     * A numeric row. Returns null when the dd file of this driver revision does not
     * carry the property, rather than letting PropertyPicker dereference undefined.
     */
    picker(key, step) {
        const p = this.get(key);
        if (!p) return null;
        return (
            <PropertyPicker
                key={key}
                property={p}
                title={this.label(p)}
                measureUnit={p.info.unit}
                min={p.valueMeta.min}
                max={p.valueMeta.max}
                step={step}
                handleChange={this.handleChange} />
        );
    }

    dropdown(key, handleChange) {
        const p = this.get(key);
        if (!p) return null;
        return (
            <PropertyDropdown
                key={key}
                property={p}
                properties={p.valueMeta.availableValues}
                propertyDisplayFunc={(v) => this.tr(v)}
                title={this.label(p)}
                measureUnit={p.info.unit}
                handleChange={handleChange || this.handleChange} />
        );
    }

    systemVoltage() {
        const p = this.get(MPPT.systemVoltage);
        if (!p) return null;

        // Derived from availableValues rather than hardcoded, so a dd file that renames or
        // drops a value degrades instead of showing a dead option.
        const options = [];
        (p.valueMeta.availableValues || []).forEach((v) => {
            if (v === 'Auto') {
                options.push('Auto-12V', 'Auto-24V');
            } else {
                options.push(v);
            }
        });

        const shown = p.value === 'Auto' ? `Auto-${this.state.autoBand}` : p.value;

        return (
            <PropertyDropdown
                key={MPPT.systemVoltage}
                property={{ ...p, value: shown }}
                properties={options}
                propertyDisplayFunc={(v) => this.tr(v)}
                title={this.label(p)}
                measureUnit={p.info.unit}
                handleChange={this.handleSystemVoltage} />
        );
    }

    handleSystemVoltage(propertyName, shown) {
        const auto = shown === 'Auto-12V' || shown === 'Auto-24V';
        // Remember the band across an Auto -> 12V -> Auto round trip too.
        this.setState({ autoBand: shown.replace('Auto-', '') });
        this.handleChange(propertyName, auto ? 'Auto' : shown);
        // No reset: the band selects a DIFFERENT set of registers, each already holding its
        // own value and its own default. Nothing has to be copied from one band to the other.
    }

    /** A clock-time row for a property stored as minutes since midnight. */
    time(key) {
        const p = this.get(key);
        if (!p) return null;
        return (
            <PropertyTimePicker
                key={key}
                property={p}
                title={this.label(p)}
                handleChange={this.handleChange} />
        );
    }

    toggle(key) {
        const p = this.get(key);
        if (!p) return null;
        return (
            <PropertySwitch
                key={key}
                property={p}
                title={this.label(p)}
                handleChange={this.handleChange} />
        );
    }

    /** The current value of a property, or undefined if the driver does not carry it. */
    valueOf(key) {
        const p = this.get(key);
        return p ? p.value : undefined;
    }

    render() {
        // Enum values arrive as LABELS, not numeric keys: t4tapi's
        // EnumPropertyValue.Convert2UserValue maps the programming value through
        // AvailableValues[].Label, and ValueMetaInfo.AvailableValues is a List<string> of
        // labels too. So compare against "LiFePO" / "12V" / "LEDs" / "User", never "0"/"1".
        const chemistry = this.valueOf(MPPT.batteryType);              // Lead-acid | LiFePO
        const tempMode = this.valueOf(MPPT.temperatureProtectionMode); // Battery | LEDs
        const outputLevel = this.valueOf(MPPT.intelligentOutputAdjustment);
        const lighting = this.valueOf(MPPT.lightingMode);              // PV Priority | RTC Time Priority | Both PV and RTC Time
        const dstOn = this.valueOf(MPPT.daylightSavingTime) === '1';

        // A selector the driver does not carry tells us nothing, so fall back to showing
        // everything rather than silently hiding registers. Errors on a hidden field are
        // still reported against the feature in the side panel, so visibility does not
        // need to defend against them.
        const unknown = (v) => v === undefined || v === null || v === '';

        // Auto is split into Auto-12V / Auto-24V in the dropdown (see systemVoltage()), so
        // the screen always knows which band the battery voltages are in and never has to
        // offer both. It decides their RANGE, not which register they are - see
        // batteryVoltage().
        const systemVoltage = this.valueOf(MPPT.systemVoltage);
        const band = systemVoltage === 'Auto' ? this.state.autoBand : systemVoltage;
        const show12 = unknown(band) || band === '12V';
        const show24 = unknown(band) || band === '24V';
        const showLead = unknown(chemistry) || chemistry === 'Lead-acid';
        const showLifepo = unknown(chemistry) || chemistry === 'LiFePO';

        const ledBranch = tempMode === 'LEDs';
        const batteryBranch = unknown(tempMode) || tempMode === 'Battery';
        const showDerating = unknown(outputLevel) || outputLevel === 'User';
        // app_settings: "Shown only when Lighting Mode is not 0", i.e. anything but PV
        // Priority. Compared against the dd EnumValue Label, which is always English - tr()
        // only changes what is drawn.
        const showSunTimes = unknown(lighting) || lighting !== 'PV Priority';

        const selectors = [MPPT.batteryType, MPPT.systemVoltage, MPPT.maxChargingCurrent];
        // The four registers of the selected combination. All sixteen exist in the dd file,
        // each with its own address, range and default, so choosing a combination is a plain
        // lookup - there is nothing to narrow, convert or reset. Under Auto the driver decides
        // the pack itself, so autoBand picks which set to show.
        const voltages = [].concat(
            show12 && showLifepo ? [
                MPPT.lithium12OverDischargeProtection, MPPT.lithium12OverDischargeRecovery,
                MPPT.lithium12FullChargeCutoff, MPPT.lithium12FullChargeRecovery] : [],
            show12 && showLead ? [
                MPPT.leadAcid12OverDischargeProtection, MPPT.leadAcid12OverDischargeRecovery,
                MPPT.leadAcid12BoostCharge, MPPT.leadAcid12FloatCharge] : [],
            show24 && showLifepo ? [
                MPPT.lithium24OverDischargeProtection, MPPT.lithium24OverDischargeRecovery,
                MPPT.lithium24FullChargeCutoff, MPPT.lithium24FullChargeRecovery] : [],
            show24 && showLead ? [
                MPPT.leadAcid24OverDischargeProtection, MPPT.leadAcid24OverDischargeRecovery,
                MPPT.leadAcid24BoostCharge, MPPT.leadAcid24FloatCharge] : []);
        const pv = [MPPT.sleepMode, MPPT.lightControlVoltage, MPPT.lightControlDelay];
        const timeBase = [MPPT.lightingMode, MPPT.daylightSavingTime]
            .concat(showSunTimes ? [MPPT.sunriseTime, MPPT.sunsetTime] : [])
            .concat(dstOn ? [MPPT.regionTimeZone] : [MPPT.timeZone]);
        // T5..T8 each pair a threshold with its own current limit, which is how the app
        // lists them - the temperature alone does not say what it limits to.
        const batteryTemps = [
            MPPT.chargingStopHigh, MPPT.chargingStopLow,
            MPPT.dischargingStopHigh, MPPT.dischargingStopLow,
            MPPT.chargingLimitHigh, MPPT.chargeLimitCurrent1,
            MPPT.chargingLimitLow, MPPT.chargeLimitCurrent2,
            MPPT.dischargingLimitHigh, MPPT.dischargeLimitCurrent1,
            MPPT.dischargingLimitLow, MPPT.dischargeLimitCurrent2,
        ];
        // Output Derating Current belongs to the LED branch, not the battery limits.
        const ledTemps = [
            MPPT.ledTemperatureProtection, MPPT.ledTemperatureRecovery,
            MPPT.deratingDischargeCurrentLimit,
        ];
        const derating = [
            MPPT.deratingStartVoltage, MPPT.deratingEndVoltage, MPPT.deratingEndCurrent,
        ];
        const output = [MPPT.intelligentOutputAdjustment];
        const advance = [MPPT.batteryLeadResistance];

        // Each column is its own flex stack, so a card is as tall as its content and the next
        // one starts right underneath - see components/CardMasonry. h-auto overrides FeatureCard's
        // h-full (tailwind-merge, later wins), which would otherwise stretch the last card in
        // a column to fill it. Spacing is the column's gap-4, not a margin here.
        const span = 'h-auto w-full';
        // align-content: start - the body grid's implicit rows default to stretch, which
        // divided spare height between the rows instead of leaving it at the bottom.
        const bodyTop = 'content-start';

        // Returns { node, weight } rather than a bare element: CardMasonry needs a height for
        // each card, and since every card is a header plus N uniform field rows, the row
        // count is the height - no measuring, no layout pass, no refs.
        /**
         * @param layoutRows the row count the masonry should PLAN for, when it differs from
         *        what is on screen right now. Give it the most the card can ever show.
         *
         * Without it the weight tracked the visible rows, so choosing PV Priority (which hides
         * Sunrise and Sunset) re-balanced the columns and threw whole cards - including the
         * Lighting Mode dropdown the user had just used - into a different column. A control
         * must not move because of what you selected in it.
         *
         * Planning for the maximum makes placement depend only on WHICH cards exist, never on
         * the current selection, so a card keeps its column for the life of the screen. Cards
         * below it still slide up when one shrinks, which is ordinary reflow and readable; the
         * cost is slightly looser balance when a card is showing its short form.
         */
        const card = (title, keys, rows, layoutRows) => {
            // picker/dropdown/toggle return null for a property this driver does not carry,
            // so a whole group can come back empty - e.g. the 24V registers before a dd
            // file that has them is released. Render nothing rather than an empty box.
            const present = rows.filter(Boolean);
            if (present.length === 0) {
                return null;
            }
            return {
                weight: 1 + (layoutRows === undefined ? present.length : layoutRows),
                node: (
                    <FeatureCard
                        key={title}
                        variant="modern"
                        title={title}
                        bodyLayout="fields"
                        bodyClassName={bodyTop}
                        className={cn(span, this.cardState(this.present(keys)))}>
                        {present}
                    </FeatureCard>
                ),
            };
        };

        const batteryTempRows = [
            this.picker(MPPT.chargingStopHigh, 1),
            this.picker(MPPT.chargingStopLow, 1),
            this.picker(MPPT.dischargingStopHigh, 1),
            this.picker(MPPT.dischargingStopLow, 1),
            this.picker(MPPT.chargingLimitHigh, 1),
            this.picker(MPPT.chargeLimitCurrent1, 0.01),
            this.picker(MPPT.chargingLimitLow, 1),
            this.picker(MPPT.chargeLimitCurrent2, 0.01),
            this.picker(MPPT.dischargingLimitHigh, 1),
            this.picker(MPPT.dischargeLimitCurrent1, 0.01),
            this.picker(MPPT.dischargingLimitLow, 1),
            this.picker(MPPT.dischargeLimitCurrent2, 0.01),
        ];
        const ledTempRows = [
            this.picker(MPPT.ledTemperatureProtection, 1),
            this.picker(MPPT.ledTemperatureRecovery, 1),
            this.picker(MPPT.deratingDischargeCurrentLimit, 1),
        ];
        const deratingRows = [
            this.picker(MPPT.deratingStartVoltage, 0.01),
            this.picker(MPPT.deratingEndVoltage, 0.01),
            this.picker(MPPT.deratingEndCurrent, 0.01),
        ];

        const cards = [
            card('Battery', selectors.concat(voltages), [
                this.dropdown(MPPT.batteryType),
                this.systemVoltage(),
                this.picker(MPPT.maxChargingCurrent, 0.01),
            ].concat(voltages.map((k) => this.picker(k, 0.01))),
            selectors.length + 4),

            card('PV Input and Light Control', pv, [
                this.toggle(MPPT.sleepMode),
                this.picker(MPPT.lightControlVoltage, 0.01),
                this.picker(MPPT.lightControlDelay, 1),
            ]),

            // Enable Daylight Saving Time swaps the picker: on, the firmware needs a named
            // region so it can derive the DST start/end rules itself; off, a plain UTC
            // offset is enough. Never both, as in the app.
            card('Time Base', timeBase, [
                this.dropdown(MPPT.lightingMode),
                // Stored as minutes since midnight; shown on a clock.
                ...(showSunTimes ? [
                    this.time(MPPT.sunriseTime),
                    this.time(MPPT.sunsetTime),
                ] : []),
                this.toggle(MPPT.daylightSavingTime),
                dstOn ? this.dropdown(MPPT.regionTimeZone)
                    : this.dropdown(MPPT.timeZone),
            ], 5),

            // The mode selector and its active branch share one card, so the selector
            // never disappears along with the rows it controls.
            card('Temperature Protection',
                [MPPT.temperatureProtectionMode].concat(
                    batteryBranch ? batteryTemps : [],
                    ledBranch ? ledTemps : []),
                [this.dropdown(MPPT.temperatureProtectionMode)]
                    .concat(batteryBranch ? batteryTempRows : [])
                    .concat(ledBranch ? ledTempRows : []),
                1 + Math.max(batteryTempRows.length, ledTempRows.length)),

            card('Advance', advance, [this.picker(MPPT.batteryLeadResistance, 1)]),

            // Derating Start/End Voltage and End Current apply only to the User power
            // level; the other levels are firmware-defined curves.
            card('Output and Derating',
                output.concat(showDerating ? derating : []),
                [this.dropdown(MPPT.intelligentOutputAdjustment)]
                    .concat(showDerating ? deratingRows : []),
                1 + deratingRows.length),
        ].filter(Boolean);

        // Masonry rather than the grid the other feature screens use: every card here is the
        // same width, and Temperature Protection is three times the height of the rest, which
        // in a grid left a column of dead space beside it. See components/CardMasonry.
        return (
            <div className="mx-auto w-full max-w-[1800px] px-4">
                <CardMasonry cards={cards} />
            </div>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    return {
        validationSummary: state.project.validationSummary,
        issues: state.project.issueByProperty,
    };
};

const mapDispatchToProps = {
}

export default compose(
    connect(
        mapStateToProps,
        mapDispatchToProps),
    withTranslation()
)(SolarChargeController);
